import React from 'react';
import { X, Download } from 'lucide-react';
import { ComplianceItem, ComplianceChecklist } from '../data/complianceData';
import { getRequirementLevelColor, getItemCategory } from '../utils/helpers';

interface Props {
  item: ComplianceItem;
  checklist: ComplianceChecklist;
  onStatusChange: (id: string, status: string) => void;
  onClose: () => void;
}

const COUNTRY_LABELS: Record<string, string> = {
  BE: '🇧🇪 Belgium (BE-CyFun®2023)',
  FI: '🇫🇮 Finland (Kybermittari)',
  EL: '🇬🇷 Greece (Min. Decision 1689/2025)',
  ES: '🇪🇸 Spain (ENS RD 311/2022)',
  FR: '🇫🇷 France',
};

const DetailModal: React.FC<Props> = ({ item, checklist, onStatusChange, onClose }) => {
  const handleExportJson = () => {
    const blob = new Blob([JSON.stringify(item, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `requirement-${item.id}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold text-gray-800">{item.id} - {item.title}</h2>
            <span className="inline-block mt-2 px-3 py-1 text-sm font-medium bg-blue-100 text-blue-700 rounded-full">
              {getItemCategory(item.id)}
            </span>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-6 h-6 text-gray-500" />
          </button>
        </div>

        <div className="p-6">
          <div className="flex justify-between mb-6">
            <div className="flex items-center gap-4">
              <span className="text-gray-700">Compliance Status:</span>
              <select
                value={checklist[item.id] || 'not-started'}
                onChange={(e) => onStatusChange(item.id, e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg"
              >
                <option value="not-started">Not Started</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
            <button
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2"
              onClick={handleExportJson}
            >
              <Download className="w-4 h-4" />
              Export as JSON
            </button>
          </div>

          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">International Standards Mapping</h3>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="border border-gray-200 px-4 py-2 text-left">Standard</th>
                    <th className="border border-gray-200 px-4 py-2 text-left">References</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2 font-medium">ISO 27001:2022</td>
                    <td className="border border-gray-200 px-4 py-2">{item.iso27001}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2 font-medium">NIST CSF v2.0</td>
                    <td className="border border-gray-200 px-4 py-2">{item.nist}</td>
                  </tr>
                  <tr>
                    <td className="border border-gray-200 px-4 py-2 font-medium">ETSI EN 319 401</td>
                    <td className="border border-gray-200 px-4 py-2">{item.etsi}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="border border-gray-200 px-4 py-2 font-medium">CEN/TS 18026:2024</td>
                    <td className="border border-gray-200 px-4 py-2">{item.cen}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <h3 className="text-lg font-semibold text-gray-800 mb-4">National Frameworks Mapping</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {Object.entries(item.national).map(([country, refs]) => (
              <div key={country} className="border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-700 mb-2">{COUNTRY_LABELS[country]}</h4>
                <div className="space-y-2">
                  {refs.split(',').map((ref: string, idx: number) => {
                    const trimmed = ref.trim();
                    if (trimmed.includes('BASIC:') || trimmed.includes('IMPORTANT:') || trimmed.includes('ESSENTIAL:')) {
                      const [level, ...content] = trimmed.split(':');
                      return (
                        <div key={idx} className="flex items-start">
                          <span className={`${getRequirementLevelColor(trimmed)} mr-2`}>{level}</span>
                          <span className="text-gray-700">{content.join(':')}</span>
                        </div>
                      );
                    }
                    return <div key={idx} className="text-gray-700">{trimmed}</div>;
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailModal;
